import { createBrowserHistory, createMemoryHistory } from 'history';
import { createStore } from 'vuex';
import analytics from '../../common/analytics';
import { SET_MOBILE, SET_COMPONENT, LOCATION_CHANGE } from './mutations/_types';
import {
  MOUNTED,
  FETCH_DATA,
  PUSH,
  REPLACE,
  VIEW,
  CLICK,
  HANDLE_LINK_CLICK,
} from './actions/_types';

const store = createStore({
  devtools: typeof window !== 'undefined',
});

const history =
  typeof process !== 'undefined' && process.release && process.release.name === 'node'
    ? createMemoryHistory()
    : createBrowserHistory();

const mapHistoryToData = ({ location: { pathname, search, hash }, action }) => ({
  action,
  fullPath: `${pathname}${search}${hash}`,
  pathname,
  search,
  hash,
});

history.listen((location, action) => {
  store.commit(LOCATION_CHANGE);

  // Ignore the redirect calls
  if (action === 'REPLACE') return;

  store.dispatch(FETCH_DATA);
});

const isModifiedEvent = (event) =>
  !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);

const shouldHandleClick = (event) =>
  !event.defaultPrevented && // onClick prevented default
  event.button === 0 && // ignore everything but left clicks
  (!event.currentTarget.target || event.currentTarget.target === '_self') &&
  // let browser handle "target=_blank" etc.
  !isModifiedEvent(event); // ignore clicks with modifier keys

store.registerModule('root', {
  state: () => ({
    isMobile: false,
    component: {},
    ...mapHistoryToData(history),
  }),
  mutations: {
    [SET_MOBILE]: (state, isMobile) => {
      state.isMobile = isMobile;
    },
    [SET_COMPONENT]: (state, component) => {
      state.component = component;
    },
    [LOCATION_CHANGE]: (state) => {
      Object.assign(state, mapHistoryToData(history));
    },
  },
  actions: {
    [MOUNTED]: ({ commit }) => {
      const mq = window.matchMedia('(max-width: 767px)');
      const listener = (e) => {
        commit(SET_MOBILE, e.matches);
      };
      mq.addListener(listener);
      listener({ matches: mq.matches });
    },
    [FETCH_DATA]: ({ state: { component } }) => {
      if (component.asyncData) component.asyncData({ store });
    },
    [PUSH]: (context, payload) => history.push(payload),
    [REPLACE]: (context, path) => history.replace(path),
    [VIEW]: (context, payload) => {
      setTimeout(() => {
        analytics.view(payload);
      }, 250);
    },
    [CLICK]: (context, payload) => analytics.click(payload),
    [HANDLE_LINK_CLICK]: ({ dispatch }, { event, to, before, external }) => {
      if (!shouldHandleClick(event)) return;
      event.preventDefault();
      if (external) {
        Promise.resolve(before).then(() => {
          window.location.href = to;
        });
      } else {
        dispatch(PUSH, to);
      }
    },
  },
});

export default (name, vuexModule, component) => {
  store.registerModule(name, vuexModule);

  store.commit(SET_COMPONENT, component);

  return store;
};
